// [PURPOSE]: Pure helpers for mutating playerStats._perRestSpellUses — the
//   per-character usage counter for spells granted via the perRestSpell
//   effect type. Kept out of effectCalculator (read-side) to keep the
//   service stateless.
// [WHY]: spellCastingService decrements on cast, RestPanel resets on rest,
//   future SpellsPanel UI may also call these. Centralizing the mutation
//   prevents drift between consumers.
// [INTEGRATION]: Returned objects are NEW playerStats — pass to setPlayerStats.
// [Phase 4b BC-13 follow-up — APR 2026]: Closes the per-rest spell-grant lifecycle.

/**
 * Decrement the usage counter for a per-rest spell. Returns a new playerStats
 * object with `_perRestSpellUses[spellName]` reduced by 1 (clamped at 0).
 * If the spell has no usage record yet, this initializes it to (maxUses - 1).
 *
 * @param {Object} playerStats
 * @param {string} spellName
 * @param {number} maxUses (defaults to 1)
 * @returns {Object} new playerStats
 */
export function consumePerRestSpellUse(playerStats, spellName, maxUses = 1) {
  if (!playerStats || !spellName) return playerStats;
  const uses = { ...(playerStats._perRestSpellUses || {}) };
  const current = (typeof uses[spellName] === 'number') ? uses[spellName] : maxUses;
  uses[spellName] = Math.max(0, current - 1);
  return { ...playerStats, _perRestSpellUses: uses };
}

/**
 * Restore usage counters for spells whose recharge interval matches the rest
 * type. Each PerRestSpellEntry must include `spellName` and `recharge`. The
 * `maxUses` field tells the resetter what value to clamp to.
 *
 * Returns a new playerStats with the matching _perRestSpellUses entries reset.
 *
 * @param {Object} playerStats
 * @param {Array<{spellName: string, recharge: string, maxUses: number}>} perRestSpellEntries
 * @param {'short_rest'|'long_rest'} restType
 * @returns {Object} new playerStats
 */
export function resetPerRestSpellsForRest(playerStats, perRestSpellEntries, restType) {
  if (!playerStats || !Array.isArray(perRestSpellEntries) || perRestSpellEntries.length === 0) {
    return playerStats;
  }
  const uses = { ...(playerStats._perRestSpellUses || {}) };
  let mutated = false;
  for (const entry of perRestSpellEntries) {
    if (!entry || !entry.spellName) continue;
    // long_rest covers BOTH short and long; short_rest covers only short.
    const matches = (entry.recharge === 'short_rest')
      || (entry.recharge === 'long_rest' && restType === 'long_rest');
    if (!matches) continue;
    uses[entry.spellName] = entry.maxUses ?? 1;
    mutated = true;
  }
  if (!mutated) return playerStats;
  return { ...playerStats, _perRestSpellUses: uses };
}

/**
 * Read-side helper: how many uses remain for the given spell name? Returns
 * `maxUses` if the spell has no recorded usage yet.
 */
export function getRemainingUses(playerStats, spellName, maxUses = 1) {
  if (!playerStats || !spellName) return 0;
  const recorded = playerStats._perRestSpellUses?.[spellName];
  if (typeof recorded !== 'number') return maxUses;
  return Math.max(0, Math.min(recorded, maxUses));
}
